import {Announcements} from '#/components/announcements/announcements';
import {FeedbackButton} from '#/components/feedback/feedback-floating-button';
import MenuBox from '#/components/menu/components';
import {useTestnetAccountFunding} from '#/components/testnet-account-funding/hooks/use-testnet-account-funding';
import {useTestnetNativeTokensFunding} from '#/components/testnet-account-funding/hooks/use-testnet-native-tokens-funding';
import {ExchangeContexts} from '#/contexts/app-contexts';
import {useModalContext} from '#/contexts/modal-provider';
import {UIStatusContext} from '#/contexts/ui-status-provider';
import {WalletContext} from '#/contexts/wallet-provider';
import environment from '#/environment/environment';
import {useExternalScript} from '#/hooks/query/use-external-script';
import {useApiLogin} from '#/hooks/use-api-login';
import {useOnCountdownEnd} from '#/hooks/use-countdown';
import {useSurveyModal} from '#/hooks/use-survey-modal';
import {useTrackAnalyticsPage} from '#/hooks/use-track-analytics-page';
import {useIsConnectedToTargetChain} from '#/hooks/wagmi/use-is-connected-to-target-chain';
import {MetamaskCannotReconnectPage} from '#/pages/metamask-cannot-reconnect';
import {SigninWalletToken} from '#/pages/sign-in-wallet-token';
import {SwitchNetworkPage} from '#/pages/switch-network';
import {loadTrackersAndAnalytics} from '#/utils/load-trackers-and-analytics';
import {DefaultChildren} from '#/utils/utils';
import {AnimatePresence} from 'framer-motion';
import {FunctionComponent, useContext, useEffect, useState} from 'react';
import {Outlet, useLocation} from 'react-router';

export const ExchangeRoutesElement: FunctionComponent = () => (
  <ExchangeContexts>
    <ExchangeLifecycle>
      <InstallAsStandaloneOverlaySoftRedirect>
        <LandingPageSoftRedirect>
          <MetamaskCannotReconnectSoftRedirect>
            <TokenSigninSoftRedirect>
              <SwitchNetworkPageSoftRedirect>
                <NonRouteElements>
                  <Outlet />
                  <FeedbackButton />
                </NonRouteElements>
              </SwitchNetworkPageSoftRedirect>
            </TokenSigninSoftRedirect>
          </MetamaskCannotReconnectSoftRedirect>
        </LandingPageSoftRedirect>
      </InstallAsStandaloneOverlaySoftRedirect>
    </ExchangeLifecycle>
  </ExchangeContexts>
);

const ExchangeLifecycle: FunctionComponent<DefaultChildren> = ({children}) => {
  useExternalScript(
    environment.celliAIAgentId
      ? `https://www.celli.ai/static/js/custom_widget.js?agentId=${environment.celliAIAgentId}&position=left`
      : undefined,
  );

  useEffect(() => {
    loadTrackersAndAnalytics();
  }, []);

  useApiLogin();
  useTrackAnalyticsPage();
  useSurveyModal();

  return environment.isWaitlistNet ? (
    <WaitlistNetLifecycle>{children}</WaitlistNetLifecycle>
  ) : (
    <RegularLifecycle>{children}</RegularLifecycle>
  );
};

const WaitlistNetLifecycle: FunctionComponent<DefaultChildren> = ({children}) => {
  return <>{children}</>;
};
const RegularLifecycle: FunctionComponent<DefaultChildren> = ({children}) => {
  useTestnetAccountFunding();
  useTestnetNativeTokensFunding();

  return <>{children}</>;
};

const NonRouteElements: FunctionComponent<DefaultChildren> = ({children}) => {
  const {isMenuOpen} = useContext(UIStatusContext);

  return (
    <>
      <AnimatePresence>{isMenuOpen && <MenuBox />}</AnimatePresence>
      <Announcements />
      {children}
    </>
  );
};

const InstallAsStandaloneOverlaySoftRedirect: FunctionComponent<DefaultChildren> = ({children}) => {
  // const {showInstallAsStandaloneIOS} = useContext(UIStatusContext);

  // if (showInstallAsStandaloneIOS) {
  //   return <InstallAsStandaloneIOS />;
  // }

  return <>{children}</>;
};

const TokenSigninSoftRedirect: FunctionComponent<DefaultChildren> = ({children}) => {
  const {showSignAppWithWallet} = useContext(UIStatusContext);

  return (
    <>
      {children}
      {showSignAppWithWallet && <SigninWalletToken />}
    </>
  );
};

const LandingPageSoftRedirect: FunctionComponent<DefaultChildren> = ({children}) => {
  const {open, close} = useModalContext();
  const {accountResult, web3Auth} = useContext(WalletContext);
  const location = useLocation();

  useEffect(() => {
    if (
      accountResult.isConnected ||
      (web3Auth?.cachedAdapter &&
        (accountResult.isConnecting || accountResult.isReconnecting || location.hash.includes('state=')))
    ) {
      close({name: 'WelcomeModal', props: {}});
    } else {
      open({name: 'WelcomeModal', props: {}});
    }
  }, [accountResult, web3Auth, location]); //eslint-disable-line

  return <>{children}</>;
};

const MetamaskCannotReconnectSoftRedirect: FunctionComponent<DefaultChildren> = ({children}) => {
  const {
    accountResult: {address, isConnected, isReconnecting},
    web3Auth,
  } = useContext(WalletContext);

  const [showPage, setShowPage] = useState(false);
  useOnCountdownEnd(() => {
    setShowPage(true);
  }, 1);

  if (
    showPage &&
    !!address &&
    isConnected &&
    isReconnecting &&
    !!web3Auth &&
    web3Auth.cachedAdapter === 'metamask' &&
    !web3Auth.connectedAdapterName
  ) {
    return <MetamaskCannotReconnectPage />;
  }
  return <>{children}</>;
};

const SwitchNetworkPageSoftRedirect: FunctionComponent<DefaultChildren> = ({children}) => {
  const {
    accountResult: {address},
  } = useContext(WalletContext);
  const isConnectedToTargetChain = useIsConnectedToTargetChain();

  if (!!address && !isConnectedToTargetChain) {
    return <SwitchNetworkPage />;
  }

  return <>{children}</>;
};
